import { apiService as dataverseService } from "./dataverseApi";
import { apiService as salesforceService } from "./salesforceServiceApi";
import { API_CONFIG } from "./apiConfig";
import { isSalesforceEnabled } from "../utils/featureFlagSalesforceUtils";

/**
 * Hybrid API Adapter
 *
 * This adapter intelligently routes API calls based on configuration:
 * - If provider is 'salesforce' AND method is in salesforceMethods list:
 *   Uses Salesforce implementation
 * - Otherwise: Uses Dataverse implementation (default)
 *
 * This allows for:
 * 1. Gradual migration from Dataverse to Salesforce
 * 2. Easy rollback by changing flagsmith Salesforce config
 * 3. Per-method control of which backend to use
 *
 * Usage in components:
 * import { apiService } from './api/apiAdapter';
 */
export const apiService = new Proxy(
  {},
  {
    get: (target, methodName) => {
      // Cache the flag check result to ensure consistency and performance
      const isSalesforce = isSalesforceEnabled();

      // Check if we should use Salesforce for this method
      const useSalesforce =
        isSalesforce &&
        API_CONFIG.salesforceMethods.includes(methodName) &&
        salesforceService[methodName]; // Ensure method exists in Salesforce

      if (useSalesforce) {
        console.log(`🔵 Using Salesforce API for: ${methodName}`);
        return salesforceService[methodName];
      } else {
        console.log(`🟢 Using Dataverse API for: ${methodName}`);
        // Default to Dataverse
        if (!isSalesforce && !salesforceService[methodName]) {
          console.log(`🟡 Method '${methodName}' not in Salesforce, using Dataverse`);
        }
        return dataverseService[methodName];
      }
    },
  },
);

/**
 * Get the current API provider being used
 */
export const getCurrentProvider = () => (isSalesforceEnabled() ? "salesforce" : "dataverse");

/**
 * Check if a specific method is using Salesforce
 */
export const isUsingSalesforce = (methodName) => {
  return (
    isSalesforceEnabled() &&
    API_CONFIG.salesforceMethods.includes(methodName) &&
    !!salesforceService[methodName]
  );
};
